import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PtLuxonDate } from '../../models/projects/date.models';
import { SetupService } from '../setup/setup.service';
import {
  DailyProgressReportSummary, DprSignOffRequest
} from '../../models/daily-progress-report/daily-progress-report.models';
import { VesselService } from '../vessel/vessel.service';
import { Observable } from 'rxjs/Observable';
import { AuthService } from '../../_foundation/services/auth/auth.service';
import { User } from '../../_foundation/models/security.models';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { map } from 'rxjs/operators';
import { SignatureDto, SignatureRole, SignaturesPayload } from '../../models/dpr-signatures/dpr-signatures.models';
import { UpdatedSetupSummary } from '../../models/setup/setup.models';

@Injectable()
export class DailyProgressReportService {
  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private setupService: SetupService,
    private vesselService: VesselService,
    private authService: AuthService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {

  }

  async getCurrentDprVersionNumber(date: PtLuxonDate): Promise<Observable<string>> {
    const dateLong = date.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/dailyprogressreport/getcurrentversionnumber/${dateLong}`;

    return this.http.get<string>(url);
  }

  async getDprSummary(ptLuxonDate: PtLuxonDate, loadedSetupSummary?: UpdatedSetupSummary): Promise<DailyProgressReportSummary> {
    if (ptLuxonDate === null) {
      return null;
    }

    const progressReportSummary: DailyProgressReportSummary = new DailyProgressReportSummary();

    const setupSummary = loadedSetupSummary || await this.setupService.getSetupSummary(ptLuxonDate, true);
    progressReportSummary.updatedSetupSummary = setupSummary;

    const vesselEditor = await this.vesselService.getEditor();
    progressReportSummary.vesselName = vesselEditor.vesselName;
    progressReportSummary.clientName = loadedSetupSummary?.projectClientName != null
      ? loadedSetupSummary.projectClientName
      : await this.setupService.getClientForProject(setupSummary.projectId);

    return progressReportSummary;
  }

  getCurrentDprDownloadLink(date: PtLuxonDate): string {
    const dateLong = date.dateTime.toMillis();
    return `${this.config.apiUrl}api/dailyprogressreport/getcurrentdpr/${dateLong}`;
  }

  getPreviewDprDownloadLink(date: PtLuxonDate, vesselId: number, isImplementer: boolean): string {
    const dateLong = date.dateTime.toMillis();
    const user = this.authService.currentUser<User>();

    return `${this.config.apiUrl}api/dailyprogressreport/${dateLong}/${vesselId}/${isImplementer}/${user.id}/true/${null}/generate-new`;
  }

  getDprPreviewFile(date: PtLuxonDate, vesselId: number, isImplementer: boolean, refresh = false) {
    const url = this.getPreviewDprDownloadLink(date, vesselId, isImplementer);
    return this.getDprFile(url + (refresh ? '?review=' + Date.now() : ''), 'DPR Preview.pdf');
  }

  getCurrentDprFile(date: PtLuxonDate) {
    return this.getDprFile(this.getCurrentDprDownloadLink(date), 'DailyProgressReport.pdf');
  }

  private async getDprFile(url: string, fallbackFilename: string): Promise<{ report: Blob; filename: string }> {
    const response = await this.http.get(url, { observe: 'response', responseType: 'blob' }).toPromise();
    if (!response.body || !response.body.size) { throw new Error('The DPR download returned an empty report.'); }
    return { report: response.body, filename: response.headers.get('X-Filename') || fallbackFilename };
  }

  getNewDprDownloadLink(date: PtLuxonDate, vesselId: number, isImplementer: boolean, signatureDate?: DateTime): string {
    const dateLong = date.dateTime.toMillis();
    const signatureDateLong = signatureDate ? signatureDate.toMillis() : null;

    return `${this.config.apiUrl}api/dailyprogressreport/${dateLong}/${vesselId}/${isImplementer}/${this.authService.currentUser<User>().id}/false/${signatureDateLong}/generate-new`;
  }

  async generateDpr(date: PtLuxonDate, vesselId: number, isImplementer: boolean): Promise<{ report: Blob; filename: string }> {
    const response = await this.http.get(this.getNewDprDownloadLink(date, vesselId, isImplementer), {
      observe: 'response', responseType: 'blob'
    }).toPromise();
    if (!response.body || !response.body.size) {
      throw new Error('DPR generation returned an empty report.');
    }
    return { report: response.body, filename: response.headers.get('X-Filename') || 'DailyProgressReport.pdf' };
  }

  getSignOffDprLink(date: PtLuxonDate, vesselId: number, isImplementer: boolean, signatureDate?: DateTime): string {
    const dateLong = date.dateTime.toMillis();
    const signatureDateLong = signatureDate ? signatureDate.toMillis() : null;

    return `${this.config.apiUrl}api/dailyprogressreport/${dateLong}/${vesselId}/${isImplementer}/${this.authService.currentUser<User>().id}/${signatureDateLong}/sign-off`;
  }


  public signOffDpr(date: PtLuxonDate, vesselId: number, isImplementer: boolean, signatureDate?: DateTime, clientComments?: string, signatures?: SignaturesPayload): Observable<any> {
    const url = `${this.config.apiUrl}api/dailyprogressreport/sign-off`;

    const toDto = (role: SignatureRole, dataUrl?: string): SignatureDto | null => {
      if (!dataUrl) return null;
      const comma = dataUrl.indexOf(',');
      if (comma < 0) return null;
      const header = dataUrl.substring(0, comma);
      const base64 = dataUrl.substring(comma + 1);
      const mime = header.split(':')[1]?.split(';')[0] || 'image/png';
      return { role, base64Png: base64, mimeType: mime };
    };

    const sigs: SignatureDto[] = []
      .concat(toDto('Master', signatures?.Master) || [])
      .concat(toDto('Client', signatures?.Client) || [])
      .concat(toDto('Other',  signatures?.Other)  || []);

    const request: any = {
      date: this.dateParserService.removeOffsetFromLuxonDate(date.dateTime),
      signatureDate,
      clientComments,
      vesselId,
      userId: this.authService.currentUser<User>().id,
      isImplementer,
      signatures: sigs.length ? sigs : undefined
    };

    return this.http.post(url, request, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      map(response => {
        const filename = response.headers.get('X-Filename') || 'DailyProgressReport.pdf';
        return {
          report: new Blob([response.body!], { type: 'application/pdf' }),
          filename
        };
      })
    );
  }

  hasSignedOffDpr(date: PtLuxonDate): Promise<boolean> {
    const dateLong = date.dateTime.toMillis();

    const url = `${this.config.apiUrl}api/dailyprogressreport/${dateLong}/hassignedoff`;

    return this.http.get<boolean>(url).toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(() => {
        return Promise.resolve(false);
      });
  }

  async getCurrentSignatureDateForDay(dailyLogDate: PtLuxonDate): Promise<DateTime> {
    const dateLong = dailyLogDate.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/dailyprogressreport/${dateLong}/latest/signature-date`;
    const signatureDateString = await this.http.get<string>(url).toPromise();

    return await this.dateParserService.deserializeDate(signatureDateString, true, this.timeZoneService);
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-progress-report/daily-progress-report.service.ts